import { useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { motion } from 'framer-motion';
import { FlaskConical } from 'lucide-react';
import { supabase } from '../../lib/supabase';
import { API_BASE } from '../../lib/apiBase';

async function authHeaders(): Promise<Record<string, string>> {
  const { data } = await supabase.auth.getSession();
  const token = data.session?.access_token;
  if (!token) throw new Error('Not authenticated');
  return { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' };
}

export default function TestSurveyPage() {
  const { surveyId } = useParams();
  const navigate = useNavigate();
  const [simulating, setSimulating] = useState<string | null>(null);

  const { data: surveyData, isLoading } = useQuery({
    queryKey: ['test-survey', surveyId],
    queryFn: async () => {
      const headers = await authHeaders();
      // Try admin endpoint first, fall back gracefully
      const res = await fetch(`${API_BASE}/api/admin/surveys/test/${surveyId}`, { headers });
      if (res.ok) return res.json();
      // Non-admin: return minimal data from survey ID
      return { survey: { id: surveyId, title: 'Test Survey', points_value: 0, estimated_length_minutes: 0 } };
    },
    enabled: Boolean(surveyId),
  });

  async function simulate(outcome: string) {
    setSimulating(outcome);
    try {
      const headers = await authHeaders();
      const res = await fetch(`${API_BASE}/api/surveys/test/${surveyId}/simulate`, {
        method: 'POST',
        headers,
        body: JSON.stringify({ outcome }),
      });
      if (!res.ok) throw new Error('Simulation failed');
      const data = await res.json();
      window.location.href = data.redirectUrl;
    } catch {
      setSimulating(null);
    }
  }

  if (isLoading) {
    return (
      <div className="min-h-screen bg-[#F8F9FA] flex items-center justify-center">
        <div className="text-[#6B7280]">Loading survey...</div>
      </div>
    );
  }

  const survey = surveyData?.survey;

  return (
    <div className="min-h-screen bg-[#F8F9FA] flex items-center justify-center px-4">
      <motion.div
        initial={{ opacity: 0, y: 10 }}
        animate={{ opacity: 1, y: 0 }}
        className="bg-white rounded-2xl shadow-lg max-w-md w-full p-8 space-y-6"
      >
        <div className="text-center">
          <div className="inline-flex items-center gap-2 bg-amber-100 text-amber-700 text-xs font-bold px-3 py-1 rounded-full mb-4">
            <FlaskConical className="w-3.5 h-3.5" />
            TEST SURVEY
          </div>
          <h1 className="text-xl font-bold font-display text-[#111827]">
            {survey?.title ?? 'Test Survey'}
          </h1>
          <p className="text-sm text-[#6B7280] mt-2">
            This is a test survey for platform verification
          </p>
        </div>

        <div className="bg-[#F8F9FA] rounded-xl p-4 text-sm text-[#6B7280] space-y-1">
          <div className="flex justify-between"><span>Points:</span><span className="text-[#111827] font-medium">{survey?.points_value ?? 0} pts</span></div>
          <div className="flex justify-between"><span>Est. Length:</span><span className="text-[#111827] font-medium">{survey?.estimated_length_minutes ?? 0} min</span></div>
        </div>

        <div className="space-y-3">
          <p className="text-xs text-[#9CA3AF] text-center">Choose an outcome to test:</p>
          <button
            onClick={() => simulate('complete')}
            disabled={simulating !== null}
            className="w-full py-3 rounded-full bg-[#10B981] text-white text-sm font-medium hover:bg-[#059669] transition-colors disabled:opacity-50"
          >
            {simulating === 'complete' ? 'Simulating...' : '\u2713 Complete This Survey'}
          </button>
          <button
            onClick={() => simulate('screenout')}
            disabled={simulating !== null}
            className="w-full py-3 rounded-full bg-[#F59E0B] text-white text-sm font-medium hover:bg-[#D97706] transition-colors disabled:opacity-50"
          >
            {simulating === 'screenout' ? 'Simulating...' : '\u2717 Get Screened Out'}
          </button>
          <button
            onClick={() => simulate('quotafull')}
            disabled={simulating !== null}
            className="w-full py-3 rounded-full bg-[#3B82F6] text-white text-sm font-medium hover:bg-[#2563EB] transition-colors disabled:opacity-50"
          >
            {simulating === 'quotafull' ? 'Simulating...' : '\u25CB Quota Full'}
          </button>
          <button
            onClick={() => simulate('security-terminate')}
            disabled={simulating !== null}
            className="w-full py-3 rounded-full bg-[#6B7280] text-white text-sm font-medium hover:bg-[#4B5563] transition-colors disabled:opacity-50"
          >
            {simulating === 'security-terminate' ? 'Simulating...' : '\u2298 Security Terminate'}
          </button>
        </div>

        <button
          onClick={() => navigate('/dashboard')}
          className="w-full text-sm text-[#6B7280] hover:text-[#111827] text-center"
        >
          Back to Dashboard
        </button>
      </motion.div>
    </div>
  );
}
